import type { ReactNode } from "react";
import { useState } from "react";
import {
  BackLink,
  Button,
  Checkbox,
  Container,
  ErrorSummary,
  PortalLayout,
  PortalPanel,
  PortalPanelBody,
  ResponsiveText,
  SectionListCard,
  Stack,
  StatusPanel,
  Stepper,
  Typography,
} from "../../design-system";
import type { ErrorSummaryItem, SectionListCardRow, SectionListRowMarker, StepperItemData } from "../../design-system";
import { renewalCaptureContent as content } from "./content";
import { completedRowCount, sectionTitleId, tickId } from "./section-status";
import type { SectionListRow } from "./section-status";

/** Maps this feature's own five-value `SectionStatus` to the design
 * system's closed, presentation-only marker union (`SectionListCard`
 * never imports a feature type). */
function markerFor(row: SectionListRow): SectionListRowMarker {
  if (row.status === "completed" || row.status === "completed-by-colleague") {
    return "complete";
  }
  if (row.status === "assigned") {
    return "assigned";
  }
  if (row.status === "in-progress") {
    return "active";
  }
  return "todo";
}

export interface SectionListRouteProps {
  readonly rows: readonly SectionListRow[];
  /** AC-19: a contributor sees no stepper, eyebrow, checkboxes or assign
   * controls. */
  readonly isOwner: boolean;
  readonly businessName: string;
  readonly stepperItems?: readonly StepperItemData[];
  readonly canContinueAndSubmit: boolean;
  readonly ticked: ReadonlySet<string>;
  readonly onToggleTick: (assignmentKey: string, checked: boolean) => void;
  readonly onOpenSection: (instanceId: string) => void;
  readonly onAssign: () => void;
  readonly onReassign: (row: SectionListRow) => void;
  readonly onContinueAndSubmit: () => void;
  readonly onSubmit: () => void;
  readonly statusPanelParagraphs?: readonly string[];
  /** The list's own error summary: AC-16's no-tick error or AC-19's
   * incomplete-section errors. */
  readonly errorItems?: readonly ErrorSummaryItem[];
  readonly reloadFailureBanner?: ReactNode;
  /** WCAG 2.2 AA SC 4.1.2: whether the dialog each control opens is open. */
  readonly assignDialogOpen?: boolean;
  readonly reassignOpenKey?: string;
  readonly isSubmitting?: boolean;
  /** FIX-1 (m1-portal-design-fixes), H2 README.md:185-186: "Back to confirm
   * details" above the list view, owner only -- `undefined` renders no
   * link at all (a contributor's own `ExposuresJourneyRoute` composition
   * never supplies this prop, matching `DeclarationRoute`'s own
   * `onBack !== undefined` pattern). */
  readonly onBack?: () => void;
}

function statusLabel(row: SectionListRow): string {
  switch (row.status) {
    case "in-progress":
      return content.statusInProgress;
    case "completed":
      return content.statusCompleted;
    case "assigned":
      return content.assignedToLabel(row.assigneeName ?? "");
    case "completed-by-colleague":
      return content.completedByLabel(row.assigneeName ?? "");
    default:
      return content.statusToDo;
  }
}

/** H2's own `r.statusTone` (`04-05-GuidedExposures.dc.html`'s `statusTone:
 * doneMark ? 'default' : 'muted'`): a done row's status line reads in ink,
 * every other row's reads muted -- a two-way choice, not the five-tone
 * success/info/neutral/warning/error vocabulary `StatusIndicator` carries,
 * which this row's plain toned text never needed. */
function statusTone(row: SectionListRow): "default" | "muted" {
  return row.status === "completed" || row.status === "completed-by-colleague" ? "default" : "muted";
}

interface BuildSectionListRowsOptions {
  readonly showDelegation: boolean;
  readonly ticked: ReadonlySet<string>;
  readonly reassignOpenKey?: string | undefined;
  readonly onOpenSection: (instanceId: string) => void;
  readonly onReassign: (row: SectionListRow) => void;
  readonly onToggleTick: (assignmentKey: string, checked: boolean) => void;
}

/** Builds ONE `SectionListCard` row (S3776 helper for `buildSectionListCardRows`, itself a
 * helper for `SectionListRoute`). A top-level function, not a closure declared inside the `.map`
 * call below -- Sonar's cognitive-complexity algorithm folds a nested function's own branching
 * into its enclosing function's nesting depth (ADR-20261016-n4sb's same point, re-applied here
 * after TG-11 found it still scored 17 with this row-building logic merely moved into a `.map`
 * callback rather than out to its own function). */
function buildOneSectionListCardRow(
  row: SectionListRow,
  position: { readonly index: number; readonly total: number; readonly current: boolean; readonly titleRepeats: boolean },
  { showDelegation, ticked, reassignOpenKey, onOpenSection, onReassign, onToggleTick }: BuildSectionListRowsOptions,
): SectionListCardRow {
  // H2: only an unsettled section can be ticked -- never one completed, assigned or completed by a
  // colleague.
  const canDelegateThisRow =
    showDelegation &&
    !row.isAdditionalDetailsPage &&
    row.status !== "completed" &&
    row.status !== "assigned" &&
    row.status !== "completed-by-colleague";
  const canReassign = showDelegation && row.status === "assigned" && !row.isAdditionalDetailsPage;
  return {
    key: row.instanceId,
    testId: `section-list-row-${row.instanceId}`,
    // AC-22: the current section has the amber marker, a bold title and reads "In progress".
    marker: position.current ? "active" : markerFor(row),
    title: row.title,
    titleId: sectionTitleId(row),
    onOpen: () => onOpenSection(row.instanceId),
    openTestId: `section-list-open-${row.instanceId}`,
    current: position.current,
    positionText: content.sectionPositionText(position.index + 1, position.total),
    // Business rule 12: a policy number only where two of the listed sections share a title.
    ...(row.displayPolicyNumber !== undefined && position.titleRepeats
      ? { policyRef: content.policyNumberLabel(row.displayPolicyNumber) }
      : {}),
    statusLabel: position.current ? content.statusInProgress : statusLabel(row),
    statusDone: statusTone(row) === "default",
    ...(canReassign
      ? {
          reassign: {
            label: content.reassignLabel,
            hiddenLabel: row.title,
            onClick: () => onReassign(row),
            expanded: reassignOpenKey === row.assignmentKey,
            testId: `section-list-reassign-${row.instanceId}`,
          },
        }
      : {}),
    ...(canDelegateThisRow
      ? {
          checkbox: (
            <Checkbox
              id={tickId(row)}
              label={content.selectToAssignLabel(row.title, position.titleRepeats ? row.displayPolicyNumber : undefined)}
              checked={ticked.has(row.assignmentKey)}
              onChange={(checked) => {
                onToggleTick(row.assignmentKey, checked);
              }}
              variant="plain"
              labelVisuallyHidden
            />
          ),
        }
      : {}),
  };
}

/** Builds one `SectionListCard` row per section instance (S3776 helper for
 * `SectionListRoute`, which stays a flat sequence by delegating this
 * per-row branching here). */
function buildSectionListCardRows(
  rows: readonly SectionListRow[],
  options: BuildSectionListRowsOptions,
): SectionListCardRow[] {
  // AC-22: the first section that is not Completed, Completed by a colleague or Assigned.
  const currentIndex = rows.findIndex(
    (row) => row.status !== "completed" && row.status !== "completed-by-colleague" && row.status !== "assigned",
  );
  const titleCounts = new Map<string, number>();
  for (const row of rows) {
    if (!row.isAdditionalDetailsPage) {
      titleCounts.set(row.title, (titleCounts.get(row.title) ?? 0) + 1);
    }
  }
  return rows.map((row, index) =>
    buildOneSectionListCardRow(
      row,
      { index, total: rows.length, current: index === currentIndex, titleRepeats: (titleCounts.get(row.title) ?? 0) > 1 },
      options,
    ),
  );
}

interface SectionListCardSectionProps {
  readonly isStale: boolean;
  readonly rows: readonly SectionListRow[];
  readonly sectionListRows: SectionListCardRow[];
  readonly showDelegation: boolean;
  readonly anySelectableRow: boolean;
  readonly errorItems?: readonly ErrorSummaryItem[] | undefined;
  readonly onAssign: () => void;
  readonly assignDialogOpen: boolean;
  readonly isOwner: boolean;
  readonly canContinueAndSubmit: boolean;
  readonly onContinueAndSubmit: () => void;
  readonly onSubmit: () => void;
  readonly isSubmitting: boolean;
  readonly notice: ReactNode;
}

/** The section-list card itself (ADR-20261016-v6nd / ADR-20261016-d4wn) -- pulled out of
 * `SectionListRoute`'s own return JSX so that function's complexity does not carry this card's
 * six conditionally-rendered slot props as well as its own (S3776). Pure prop-in, element-out:
 * no state, no hooks, same as `buildOneSectionListCardRow` above. */
function SectionListCardSection({
  isStale,
  rows,
  sectionListRows,
  showDelegation,
  anySelectableRow,
  errorItems,
  onAssign,
  assignDialogOpen,
  isOwner,
  canContinueAndSubmit,
  onContinueAndSubmit,
  onSubmit,
  isSubmitting,
  notice,
}: SectionListCardSectionProps) {
  return (
    <SectionListCard
      heading={content.sectionListCardHeading}
      progress={
        isStale
          ? undefined
          : rows.some((row) => row.status === "completed-by-colleague")
            ? content.sectionsCompletedWithColleagueStatement(completedRowCount(rows), rows.length)
            : content.sectionsCompletedStatement(rows.filter((row) => row.status === "completed").length, rows.length)
      }
      progressTestId="section-list-progress"
      selectHeadLabel={showDelegation && anySelectableRow ? content.selectToAssignHeaderLabel : undefined}
      notice={notice}
      errorSummary={
        errorItems !== undefined && errorItems.length > 0 ? (
          <ErrorSummary items={errorItems} id="section-list-errors" testId="section-list-errors" />
        ) : undefined
      }
      rows={sectionListRows}
      listTestId="section-list"
      assignAction={
        showDelegation && !rows.some((row) => row.status === "completed-by-colleague") ? (
          <Button emphasis="secondary" onClick={onAssign} aria-expanded={assignDialogOpen} testId="section-list-assign">
            {content.assignToColleagueLabel}
          </Button>
        ) : undefined
      }
      continueAction={
        isOwner && canContinueAndSubmit && !isStale ? (
          <Button emphasis="primary" size="large" narrowFullWidth onClick={onContinueAndSubmit} testId="section-list-continue-and-submit">
            {content.continueAndSubmitLabel}
          </Button>
        ) : undefined
      }
      submitAction={
        isOwner ? undefined : (
          <Button emphasis="primary" size="large" narrowFullWidth onClick={onSubmit} disabled={isSubmitting} testId="section-list-submit">
            {content.submitLabel}
          </Button>
        )
      }
    />
  );
}

/**
 * H2 screen 4: the section list (AC-14, AC-16, AC-17, AC-19, AC-32). One
 * row per section instance. An additional-details page row carries no
 * checkbox: it is delegated with the section that inserted it.
 */
export function SectionListRoute({
  rows,
  isOwner,
  businessName,
  stepperItems,
  canContinueAndSubmit,
  ticked,
  onToggleTick,
  onOpenSection,
  onAssign,
  onReassign,
  onContinueAndSubmit,
  onSubmit,
  statusPanelParagraphs,
  errorItems,
  reloadFailureBanner = null,
  assignDialogOpen = false,
  reassignOpenKey,
  isSubmitting = false,
  onBack,
}: Readonly<SectionListRouteProps>) {
  // H2 README.md:183-216 (ADR -- the "What are exposures?" help panel):
  // closed by default, owner only, toggled by the 44px help icon inline
  // after the display heading below.
  const [exposuresHelpOpen, setExposuresHelpOpen] = useState(false);
  // ADR-20261028-3mxk: delegation is always on now (the build-time flag is
  // retired) -- an owner always sees assign/reassign controls.
  const showDelegation = isOwner;
  // OBJ-116/FIND-164 carried forward: a failing post-write reload means
  // the list is a stale snapshot; its progress count and the route to
  // screen 6 are withheld until a reload succeeds.
  const isStale = reloadFailureBanner !== null;
  // H2's `showSelectHead`: the card header's "Select to assign" label
  // shows only while at least one row still offers its own checkbox
  // (the same condition that checkbox's own render check below uses).
  const anySelectableRow = rows.some(
    (row) =>
      !row.isAdditionalDetailsPage && row.status !== "completed" && row.status !== "assigned" && row.status !== "completed-by-colleague",
  );
  // H2: once a colleague has completed their sections the owner sees the short intro, the
  // "N of M sections completed" count and no assign action.
  const colleagueCompletedCount = rows.filter((row) => row.status === "completed-by-colleague").length;

  const sectionListRows: SectionListCardRow[] = buildSectionListCardRows(rows, {
    showDelegation,
    ticked,
    reassignOpenKey,
    onOpenSection,
    onReassign,
    onToggleTick,
  });

  return (
    <PortalLayout businessName={businessName}>
      <Container layout="exposures">
        <>
          {reloadFailureBanner}
          {isOwner && stepperItems !== undefined ? (
            <Stepper aria-label="Renewal steps" items={stepperItems} testId="section-list-stepper" />
          ) : null}
          {/* H2 (04-05-GuidedExposures.dc.html, README.md "Back link -- above
              the card"): the back link sits above the card, between the
              stepper and the eyebrow/heading group -- the same position
              `DeclarationRoute` and `SectionPageRoute` already use. */}
          {onBack !== undefined ? (
            <BackLink label={content.backToConfirmDetailsLabel} onClick={onBack} testId="section-list-back-to-confirm-details" />
          ) : null}
          {/* H2 4&5: eyebrow, heading, intro and the help panel share one column 6px apart. */}
          <Stack gap="heading">
            {isOwner ? (
              <Typography variant="eyebrow" as="p">
                {content.sectionListEyebrow}
              </Typography>
            ) : null}
            {/* The help trigger sits beside the h1, not inside it, so the
                heading's accessible name stays "Review your exposures"
                rather than absorbing the trigger's own label. */}
            <Stack direction="row" align="center" gap="none" wrap>
              <Typography variant="display" as="h1">
                {isOwner ? (
                  <ResponsiveText narrow={content.sectionListHeadingNarrow} wide={content.sectionListHeading} />
                ) : (
                  content.contributorSectionsHeading
                )}
              </Typography>
              {isOwner ? (
                <PortalPanel
                  id="exposures-help"
                  triggerLabel={content.exposuresHelpTriggerLabel}
                  expanded={exposuresHelpOpen}
                  onToggle={() => setExposuresHelpOpen((open) => !open)}
                />
              ) : null}
            </Stack>
            <Typography variant="bodyLarge">
              {isOwner && colleagueCompletedCount === 0 ? content.sectionListIntroOwner : content.sectionListIntroContributor}
            </Typography>
            {isOwner ? (
              <PortalPanelBody id="exposures-help" expanded={exposuresHelpOpen}>
                <Typography variant="body">{content.exposuresHelpText}</Typography>
              </PortalPanelBody>
            ) : null}
          </Stack>

          {/* ADR-20261016-v6nd (AC-2's "section-list card and timeline"):
              H2's own white, hairline-bordered, radius-8 card
              (`railWrap`) around the heading, meta line, row list and
              its own primary action -- the original build rendered all
              of this directly on the page background, with no card, no
              "Your sections" heading and no per-row marker/connector.
              ADR-20261016-d4wn: the card/row layout itself lives in the
              design system's `SectionListCard` -- this route only
              supplies data and fully-built content/action slots. */}
          <SectionListCardSection
            isStale={isStale}
            rows={rows}
            sectionListRows={sectionListRows}
            showDelegation={showDelegation}
            anySelectableRow={anySelectableRow}
            errorItems={errorItems}
            onAssign={onAssign}
            assignDialogOpen={assignDialogOpen}
            isOwner={isOwner}
            canContinueAndSubmit={canContinueAndSubmit}
            onContinueAndSubmit={onContinueAndSubmit}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            notice={
              statusPanelParagraphs !== undefined && statusPanelParagraphs.length > 0 ? (
                // DEBT-20261030-7abq: a div, not <output>, which permits phrasing content only and
                // StatusPanel renders block children. role="status" keeps the polite live region.
                <div role="status">
                  {/* H2: the change notice is its help panel (PortalPanel) inside the card, after its header. */}
                  <StatusPanel paragraphs={statusPanelParagraphs} h2Panel spaced={false} testId="section-list-status-panel" />
                </div>
              ) : undefined
            }
          />
        </>
      </Container>
    </PortalLayout>
  );
}
